import React, { useState } from 'react';

import { StudentWidgetWrapper } from 'src/application/ContentZone/components/Widgets/StudentWidgetWrapper';
import { useContentRequirementsContext } from 'src/application/features/RestrictNavigation/ContentRequirementsProvider';
import { useIsMobile } from 'src/application/hooks/useIsMobile';
import { useOnMount } from 'src/application/hooks/useOnMount';

import { useGetMultipleAssetUrls } from '../../hooks/useGetMultipleAssetUrls';
import { ExistingStudentWidgetProps } from '../Widget';
import { LabeledGraphic, WidgetType } from '@ael/core-api';
import { DisplayAsset, LabeledGraphic as LabeledGraphicComponent } from '@ael/shared-components-library';
import { isVirtualTourIconId } from '@ael/virtual-tour-icon-bundle';

import './LabeledGraphicWidget.scss';

export const LabeledGraphicWidget = ({ widget }: ExistingStudentWidgetProps) => {
  const { value: labeledGraphicWidgetData } = widget as LabeledGraphic;
  const isMobile = useIsMobile();

  const assetIds = [
    labeledGraphicWidgetData.data.assetId,
    ...labeledGraphicWidgetData.data.markers.map((x) => x.assetId ?? null),
  ];

  const { getAssetDataForIndex } = useGetMultipleAssetUrls(widget._id, WidgetType.labeledGraphic, assetIds);

  const mainGraphic = getAssetDataForIndex(0);

  const { submitWidget, fulfilled } = useContentRequirementsContext();
  const fulfillCurrentWidget = () => submitWidget(widget, true);

  const [, setViewedMarkers] = useState<boolean[]>(new Array(labeledGraphicWidgetData.data.markers.length).fill(false));

  const handleMarkerClick = (index: number) => {
    if (fulfilled === false && labeledGraphicWidgetData.properties.isRequired) {
      setViewedMarkers((prev) => {
        const newViewedMarkers = [...prev];
        newViewedMarkers[index] = true;
        if (newViewedMarkers.every((viewed) => viewed)) {
          fulfillCurrentWidget();
        }
        return newViewedMarkers;
      });
    }
  };
  useOnMount(() => {
    if (labeledGraphicWidgetData.data.markers.length === 0) {
      fulfillCurrentWidget();
    }
  });

  // Loading, or file was deleted!
  if (mainGraphic === null) {
    return null;
  }

  return (
    <StudentWidgetWrapper>
      <div className="uno-w-full uno-flex uno-justify-center">
        <LabeledGraphicComponent
          imageUrl={mainGraphic.url}
          imageHeight={mainGraphic.dimensions?.height ?? 0}
          imageWidth={mainGraphic.dimensions?.width ?? 0}
          isMobile={isMobile}
          markers={labeledGraphicWidgetData.data.markers.map((marker, index) => {
            const markerAssetData = getAssetDataForIndex(index + 1);
            return {
              id: marker.id,
              x: marker.x,
              y: marker.y,
              title: marker.title,
              onClick: () => handleMarkerClick(index), // mark as completed
              description: marker.description ? (
                <div
                  className="labeled-graphic__description"
                  dangerouslySetInnerHTML={{
                    __html: marker.description,
                  }}
                />
              ) : undefined,
              markerIcon: marker.markerIcon
                ? isVirtualTourIconId(marker.markerIcon)
                  ? marker.markerIcon
                  : undefined
                : undefined,
              media: markerAssetData ? (
                <DisplayAsset
                  src={markerAssetData.url}
                  type={markerAssetData.type}
                  loaderHeight={markerAssetData.dimensions?.height}
                  loaderWidth={markerAssetData.dimensions?.width}
                  imageZoomInnerPortal
                  maxImageHeight="large"
                />
              ) : undefined,
            };
          })}
        />
      </div>
    </StudentWidgetWrapper>
  );
};
